import React, { useEffect, useMemo, useState } from 'react';
import { Container, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import '../components-css/CampaignLanding.css';
import { setLinkTag, setMetaTag } from '../utils/seo';

const MARKDOWN_URL = '/landing/campana.md';
const WHATSAPP_NUMBER = '5493513678945';

const fallbackMarkdown = `# ¿De verdad cree y conoce a Dios?

La mayoría de las personas afirma creer en Dios o conocer a Dios. Pero ¿cómo podemos saber si verdaderamente conocemos a Dios? ¿Será que cada uno puede tener una opinión distinta o debe existir una única verdad?.

Para poder responder estos interrogantes, debemos recurrir a una fuente superior de autoridad que trascienda aun lo que nosotros pensamos de la verdad. Si queremos conocer a Dios tal como él es, debemos ir a la **Biblia**, que es su Palabra, suprema fuente de autoridad.

---

## ¿Cómo muestra la Biblia al único y verdadero Dios?

* **Él es Santo:** [«Dios es luz, y no hay ningunas tinieblas en él»](https://www.bible.com/es/bible/128/1JN.1.5.NVI "1 Juan 1:5 (NVI)"). El Creador del cielo y de la tierra es un Dios puro y santo. Nunca pecó ni puede tener ningún tipo de comunión con el pecador.

* **Él es Juez Justo:** [«Jehová es tardo para la ira y grande en poder, y no tendrá por inocente al culpable»](https://www.bible.com/es/bible/128/NAM.1.3.NVI "Nahúm 1:3 (NVI)"). Un día juzgará a todo pecador que haya quebrantado sus mandamientos.

---

## ¿Cómo muestra la Palabra de Dios al hombre?

Dios creó al hombre a su imagen y semejanza, pero el hombre decidió voluntariamente rebelarse y pecar. [«Por tanto, como el pecado entró en el mundo por un hombre, y por el pecado la muerte, así la muerte pasó a todos los hombres, por cuanto todos pecaron»](https://www.bible.com/es/bible/128/ROM.5.12.NVI "Romanos 5:12 (NVI)").

### El problema de la Idolatría

El primer mandamiento dice: [«No tendrás dioses ajenos aparte de mí»](https://www.bible.com/es/bible/128/EXO.20.3.NVI "Éxodo 20:3 (NVI)"). Solemos pensar que la idolatría es solo arrodillarse ante una estatua, pero Dios llama idolatría a:

* Amar a los hijos o a la familia más que a Él.

* Poner el dinero, una banda de música o un equipo de fútbol en primer lugar.

* Cualquier palabra, acción o pensamiento que ocupe el lugar que solo a Él le corresponde.

**La consecuencia:** [«El fallar en un solo mandamiento es quebrantarlos todos»](https://www.bible.com/es/bible/128/JAS.2.10.NVI "Santiago 2:10 (NVI)"). [«Por cuanto todos pecaron, y están destituidos de la gloria de Dios»](https://www.bible.com/es/bible/128/ROM.3.23.NVI "Romanos 3:23 (NVI)"). Al pecador solo le espera el infierno, donde sufrirá justamente por toda la eternidad.

---

## La inutilidad del esfuerzo humano

Todo intento humano por ganar el favor de Dios es inútil. La Biblia enseña que:

* Nuestras obras justas son como [«trapo de inmundicia»](https://www.bible.com/es/bible/128/ISA.64.6.NVI "Isaías 64:6 (NVI)") ante Él.

* Usted no puede hacer nada para justificarse mediante la religión, la filosofía o las buenas obras.

* Confesarse ante un hombre, asistir a una iglesia o ayudar a los pobres no quitan la maldad del corazón; solo tranquilizan la conciencia.

---

## El sacrificio de Cristo: La única esperanza

Solo mediante el sacrificio de Cristo puede usted conocer a Dios.

* **Su naturaleza:** Jesucristo es Dios y se hizo hombre, humillándose hasta la muerte de cruz.

* **Su vida:** Fue el único que vivió cumpliendo la ley de Dios perfectamente, sin pecado.

* **Su obra:** Murió por nuestros pecados. [«Dios muestra su amor para con nosotros, en que siendo aún pecadores, Cristo murió por nosotros»](https://www.bible.com/es/bible/128/ROM.5.8.NVI "Romanos 5:8 (NVI)").

* **Su victoria:** Después de tres días resucitó, probando que Él es Dios.

> El castigo que usted merecía fue cargado por Cristo en la cruz.

---

## ¿De qué manera puedo ser salvado?

Para ser salvos del castigo eterno debemos:

1. **Arrepentirnos:** Abandonar el pecado de todo corazón.

2. **Creer:** Poner nuestra fe exclusivamente en Jesucristo y su obra en la cruz.

3. **Reconocer:** Admitir su incapacidad de salvarse a sí mismo y rogar a Dios por perdón.

Usted no le hace ningún favor a Dios creyendo en Él; la vida eterna es un regalo de su gracia que no merecemos.

### Tome una decisión

> [«Hoy es el día de salvación»](https://www.bible.com/es/bible/128/2CO.6.2.NVI "2 Corintios 6:2 (NVI)") (2 Corintios 6:2).

Recuerde que en su indiferencia o postergación, usted rechaza la obra de salvación y ya no tendrá más excusas.

---
`;

const CampaignLanding = () => {
  const [markdown, setMarkdown] = useState('');
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const whatsappUrl = useMemo(() => `https://wa.me/${WHATSAPP_NUMBER}`, []);
  const shareUrl = 'https://iglesiacanaldevida.com/conocer-a-dios';
  const shareTitle = '¿Quisieras conocer a Dios?';
  const shareText = 'Descubrí cómo tener una relación con Dios.';

  useEffect(() => {
    const previousPaddingTop = document.body.style.paddingTop;
    const baseUrl = 'https://iglesiacanaldevida.com';
    const pageUrl = `${baseUrl}/conocer-a-dios`;
    const title = '¿Quisieras conocer a Dios? | Iglesia Canal de Vida';
    const description = 'Descubrí cómo tener una relación con Dios.';
    const imageUrl = `${baseUrl}/assets/images/resources/og-conocer-a-dios-1200x630.png`;

    document.title = title;
    document.documentElement.setAttribute('lang', 'es-AR');

    setLinkTag('canonical', pageUrl);
    setMetaTag('name', 'description', description);
    setMetaTag('property', 'og:type', 'website');
    setMetaTag('property', 'og:site_name', 'Iglesia Canal de Vida');
    setMetaTag('property', 'og:title', title);
    setMetaTag('property', 'og:description', description);
    setMetaTag('property', 'og:url', pageUrl);
    setMetaTag('property', 'og:image', imageUrl);
    setMetaTag('property', 'og:image:width', '1200');
    setMetaTag('property', 'og:image:height', '630');
    setMetaTag('property', 'og:image:alt', '¿Quisieras conocer a Dios?');
    setMetaTag('property', 'og:locale', 'es_AR');
    setMetaTag('name', 'twitter:card', 'summary_large_image');
    setMetaTag('name', 'twitter:title', title);
    setMetaTag('name', 'twitter:description', description);
    setMetaTag('name', 'twitter:image', imageUrl);
    setMetaTag('name', 'twitter:image:alt', '¿Quisieras conocer a Dios?');

    document.body.style.paddingTop = '0px';

    return () => {
      document.body.style.paddingTop = previousPaddingTop;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadMarkdown = async () => {
      try {
        const response = await fetch(MARKDOWN_URL, { cache: 'no-store' });
        if (!response.ok) {
          throw new Error('No se pudo cargar el contenido.');
        }
        const text = await response.text();
        if (isMounted) {
          setMarkdown(text.trim());
        }
      } catch (error) {
        if (isMounted) {
          setHasError(true);
          setMarkdown(fallbackMarkdown.trim());
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadMarkdown();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleScrollToContent = () => {
    const contentElement = document.querySelector('.landing-content');
    if (contentElement) {
      contentElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleShareMessage = async () => {
    const messageForShare = `${shareText} ${shareUrl}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
        return;
      } catch (error) {
        if (error?.name === 'AbortError') {
          return;
        }
      }
    }

    const whatsappShareUrl = `https://wa.me/?text=${encodeURIComponent(messageForShare)}`;
    window.open(whatsappShareUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="page-wrapper landing-page" style={{ position: 'relative', zIndex: 1 }}>
      <div
        className="landing-global-background"
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/assets/images/backgrounds/conocer_Dios.jpg)`
        }}
      ></div>
      <div className="landing-global-overlay"></div>

      <section className="landing-hero">
        <div className="landing-hero__overlay"></div>
        <Container>
          <div className="landing-hero__content">
            <span className="landing-hero__label">Una pregunta importante</span>
            <h1 className="landing-hero__title">¿Quisieras conocer a Dios?</h1>
            <p className="landing-hero__subtitle">
              Encontrá en la Biblia respuestas claras y un camino seguro para descubrir la verdad.
            </p>
            <button className="landing-hero__scroll-btn" onClick={handleScrollToContent} aria-label="Comenzar a leer">
              <i className="fas fa-chevron-down"></i>
            </button>
          </div>
        </Container>
      </section>

      <section className="landing-content">
        <Container>
          {loading ? (
            <div className="text-center my-3">
              <Spinner animation="border" />
            </div>
          ) : (
            <>
              <div className="landing-markdown">
                {hasError && (
                  <div className="landing-alert">
                    No se pudo cargar el archivo externo. Se muestra el contenido de respaldo.
                  </div>
                )}
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    a: ({ href, title, children, ...props }) => {
                      const isBibleLink = href?.includes('bible.com/es/bible');
                      const tooltipText = title || '';

                      if (!isBibleLink) {
                        return (
                          <a {...props} href={href}>
                            {children}
                          </a>
                        );
                      }

                      return (
                        <span
                          {...props}
                          className="bible-verse"
                          tabIndex={0}
                          data-tooltip={tooltipText}
                        >
                          {children}
                          <span className="bible-verse__tooltip" role="tooltip">
                            <span className="bible-verse__quote">{tooltipText}</span>
                            <a
                              className="bible-verse__link"
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Ver en bible.com
                            </a>
                          </span>
                        </span>
                      );
                    }
                  }}
                >
                  {markdown}
                </ReactMarkdown>
              </div>
              <div className="landing-cta">
                <div className="landing-invite">
                  <h2 className="landing-invite__title">¿Te gustaría saber más?</h2>
                  <p className="landing-invite__text">
                    Si tenés preguntas o querés conocer más sobre Dios, estamos deseosos de recibirte y
                    acompañarte. Nos encantaría escucharte, responder tus dudas y ayudarte a dar el próximo paso.
                  </p>
                  <div className="landing-hero__actions">
                    <Link className="landing-button landing-button--home" to="/">
                      <span className="landing-button__icon" aria-hidden="true">
                        <i className="fas fa-church"></i>
                      </span>
                      Conocer más sobre Iglesia Canal de Vida
                    </Link>
                    <a
                      className="landing-button landing-button--whatsapp"
                      href={whatsappUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <span className="landing-button__icon" aria-hidden="true">
                        <i className="fab fa-whatsapp"></i>
                      </span>
                      Contactanos por WhatsApp
                    </a>
                    <button
                      className="landing-button landing-button--share"
                      type="button"
                      onClick={handleShareMessage}
                    >
                      <span className="landing-button__icon" aria-hidden="true">
                        <i className="fas fa-share-alt"></i>
                      </span>
                      Compartir este mensaje con otros
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </Container>
      </section>
    </div>
  );
};

export default CampaignLanding;
